import { IconChevronRight, IconX } from '@tabler/icons-react'
import { ReactNode } from 'react'
import { Fragment } from 'react/jsx-runtime'

import { AstraButton, AstraIcon, AstraParagraph, AstraTooltip, astraTwMerge, BodyVariant, SizeOptions } from '~/astra'

export type CrumbItem = {
    label: string
    afterLabel?: ReactNode
    onClick?: () => void
    'data-test'?: string
}

type Variant = 'default' | 'compact'
export type BreadcrumbsProps = {
    crumbs: CrumbItem[]
    className?: string
    variant?: Variant
}

const wrapperStyles = {
    default: 'gap-1',
    compact: 'gap-0',
} satisfies Record<Variant, string>

const buttonSizeStyles = {
    default: 'medium',
    compact: 'small',
} satisfies Record<Variant, SizeOptions>

const paragraphVariantStyles = {
    default: 'body',
    compact: 'body-small',
} satisfies Record<Variant, BodyVariant>

export const Breadcrumbs = ({ crumbs, className, variant = 'default' }: BreadcrumbsProps) => (
    <div className={astraTwMerge('flex items-center', wrapperStyles[variant], className)} data-test="breadcrumbs">
        {crumbs.map((crumb, index) => (
            <Fragment key={crumb.label}>
                <div className="flex items-center gap-1">
                    {crumb.onClick ? (
                        <AstraButton
                            variant="ghost"
                            size={buttonSizeStyles[variant]}
                            onClick={crumb.onClick}
                            label={crumb.label}
                            data-test={crumb['data-test']}
                        />
                    ) : (
                        <AstraParagraph
                            data-test={crumb['data-test']}
                            className="border border-transparent px-2"
                            variant={paragraphVariantStyles[variant]}
                            weight="medium"
                            aria-label={crumb.label}
                        >
                            {crumb.label}
                        </AstraParagraph>
                    )}
                    {crumb.afterLabel && crumb.onClick ? (
                        <div onClick={crumb.onClick} className="cursor-pointer">
                            {crumb.afterLabel}
                        </div>
                    ) : (
                        crumb.afterLabel
                    )}
                </div>
                {index < crumbs.length - 1 && <AstraIcon color="text-subtle" icon={IconChevronRight} size="medium" />}
            </Fragment>
        ))}
    </div>
)

export type BreadcrumbsHeaderProps = BreadcrumbsProps & {
    className?: string
    'data-test'?: string
    onClose?: () => void
}

export const BreadcrumbsHeader = ({ className, 'data-test': dataTest, onClose, ...breadcrumbsProps }: BreadcrumbsHeaderProps) => (
    <div className={astraTwMerge('flex items-center justify-between border-b border-base px-2 py-3 text-default', className)} data-test={dataTest}>
        <Breadcrumbs {...breadcrumbsProps} />
        {onClose && (
            <div className="border-l border-base pl-2">
                <AstraTooltip tooltipContent="Lukk">
                    <AstraButton variant="ghost" icon={<AstraIcon icon={IconX} size="small" />} aria-label="Lukk" onClick={onClose} />
                </AstraTooltip>
            </div>
        )}
    </div>
)
